import { useMemo, useCallback } from 'react';
import { Copy, MessageCircle, Send, Mail } from 'lucide-react';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { safeToastError, safeToastSuccess } from '@/components/ui/toastManager';

interface ShareFallbackSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareTitle: string;
  shareText: string;
}

const buildShareMessage = (title: string, text: string) => {
  const trimmedText = text.trim();
  const trimmedTitle = title.trim();

  if (!trimmedTitle) {
    return trimmedText;
  }

  if (!trimmedText) {
    return trimmedTitle;
  }

  return `${trimmedTitle}\n\n${trimmedText}`;
};

const ShareFallbackSheet = ({
  open,
  onOpenChange,
  shareTitle,
  shareText,
}: ShareFallbackSheetProps) => {
  const message = useMemo(() => buildShareMessage(shareTitle, shareText), [shareTitle, shareText]);
  const encodedMessage = useMemo(() => encodeURIComponent(message), [message]);
  const encodedSubject = useMemo(() => encodeURIComponent(shareTitle.trim() || 'Lista della spesa'), [shareTitle]);

  const handleCopy = useCallback(async () => {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('Clipboard API non disponibile');
      }

      await navigator.clipboard.writeText(message);
      safeToastSuccess('Lista copiata negli appunti');
      onOpenChange(false);
    } catch (error) {
      console.error('Impossibile copiare la lista', error);
      safeToastError('Non è stato possibile copiare la lista. Riprovare.');
    }
  }, [message, onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="space-y-4 pb-safe-extra">
        <SheetHeader>
          <SheetTitle>Condivisione manuale</SheetTitle>
          <SheetDescription>
            La condivisione rapida non è disponibile su questo dispositivo. Usa una delle opzioni seguenti.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-3">
          <Button variant="secondary" className="justify-start gap-3" onClick={handleCopy} data-testid="share-copy-button">
            <Copy className="h-4 w-4" />
            Copia negli appunti
          </Button>

          <Button variant="outline" className="justify-start gap-3" asChild data-testid="share-whatsapp-link">
            <a href={`https://wa.me/?text=${encodedMessage}`} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="h-4 w-4" />
              Invia su WhatsApp
            </a>
          </Button>

          <Button variant="outline" className="justify-start gap-3" asChild data-testid="share-telegram-link">
            <a href={`tg://msg_url?text=${encodedMessage}`}>
              <Send className="h-4 w-4" />
              Invia su Telegram
            </a>
          </Button>

          <Button variant="outline" className="justify-start gap-3" asChild data-testid="share-email-link">
            <a
              href={`mailto:?subject=${encodedSubject}&body=${encodedMessage}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Mail className="h-4 w-4" />
              Invia via email
            </a>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ShareFallbackSheet;
